/**
 * A parent-document credential: either the literal anonymous token of a public
 * room or a signed, expiring token minted by `/api/collaboration-tokens`.
 */
export type CollaborationCredential = string | { token: string; expiresAt: number };

/**
 * One logical room's renewal seam. The broker never touches the editor, the
 * Y.Doc or the socket - it only asks a room to present the current credential
 * and, around a page resume, to hold and then replay its outbound updates.
 */
export interface CollaborationCredentialTarget {
  readonly roomName: string;
  /**
   * Sends the current credential in band. Hocuspocus has no success ACK for
   * `sendToken()`, so the broker never waits for a confirmation event: the
   * server observes the renewal, and a denial arrives as a logical close.
   */
  sendToken(): Promise<void>;
  /** Holds outbound document updates until {@link resumeOutbound}. */
  pauseOutbound?(): void;
  /** Replays whatever was held, in order. */
  resumeOutbound?(): void;
  /** Re-runs sync step one on the *same* Y.Doc. */
  resync?(): void;
}

export type CollaborationRefreshTrigger = "initial" | "schedule" | "resume" | "manual";

/**
 * Thrown when a room asks a suspended broker for a credential.
 *
 * A suspended broker has been told the document's authorization is over, so
 * minting one more token could only produce a request the server will refuse.
 * Hocuspocus turns a throwing `token()` callback into `permissionDeniedHandler`,
 * which is the correct outcome: a room that somehow reaches the socket after
 * revocation classifies itself terminal and detaches instead of retrying (AE5).
 */
export class CollaborationCredentialSuspendedError extends Error {
  constructor(documentId: string) {
    super(`Collaboration credentials for ${documentId} are suspended`);
    this.name = "CollaborationCredentialSuspendedError";
  }
}

/**
 * Refresh cadence bounds.
 *
 * Every value is derived from a constraint that already exists in the system,
 * so it can be re-checked against its source rather than against a remembered
 * measurement:
 *
 * - `clockSkewMs = 5_000`, `jitterMs = 2_000`
 *   The renewal aims at `expiresAt - 5s - jitter(0..2s)`, matching the margin
 *   `apps/backend/src/collaboration/collaborationLimits.ts` documents as the
 *   expected client cadence (~53-55초 per 60초 token).
 *
 * - `minIntervalMs = 10_000`
 *   The server accepts at most one token sync per room per
 *   `minTokenSyncIntervalMs` (5초). Twice that floor keeps a badly skewed clock
 *   or a short-lived token from producing syncs the server would only drop.
 *
 * - `maxIntervalMs = 55_000`
 *   The hard reauthorization lease is 60초 (`leaseMs`). Renewing at 55초 keeps
 *   one full retry inside the lease even if a token is issued with a longer TTL
 *   or the local clock runs behind the server's.
 *
 * - `anonymousIntervalMs = 30_000`
 *   The literal anonymous credential never expires, so it has no expiry to
 *   schedule from. Half a lease gives a public room one missed renewal of slack
 *   while still proving liveness well inside the 60초 revalidation SLA.
 *
 * - `retryIntervalMs = 10_000`
 *   A failed refresh must not extend the previous lease (R7) and must not turn
 *   into a request storm. One retry per 10초 leaves ~5 attempts inside a lease.
 */
export const CREDENTIAL_REFRESH_BOUNDS = {
  clockSkewMs: 5_000,
  jitterMs: 2_000,
  minIntervalMs: 10_000,
  maxIntervalMs: 55_000,
  anonymousIntervalMs: 30_000,
  retryIntervalMs: 10_000,
} as const;

/** The subset of `document` the broker listens on, so tests can supply their own. */
export interface CollaborationResumeSource {
  readonly visibilityState?: string;
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

export interface CollaborationCredentialBrokerOptions {
  documentId: string;
  /** The token callback boundary owned by `EditorPage`. */
  tokenProvider: () => Promise<CollaborationCredential>;
  /**
   * Whether this broker drives the renewal clock. A broker created for a room
   * that rides another broker's schedule registers and reads credentials but
   * arms no timer, so one document never renews N times.
   */
  schedule?: boolean;
  now?: () => number;
  random?: () => number;
  resumeSource?: CollaborationResumeSource | null;
  onRefreshFailed?: (error: unknown, trigger: CollaborationRefreshTrigger) => void;
}

export interface CollaborationCredentialBroker {
  readonly documentId: string;
  readonly targetCount: number;
  readonly suspended: boolean;
  /** The current token, refreshing single-flight when the cached one is due. */
  getToken(): Promise<string>;
  /** Registers a room; the returned function unregisters it. */
  register(target: CollaborationCredentialTarget): () => void;
  /** Refreshes once and fans the result out to every registered room. */
  refreshAndFanOut(trigger?: CollaborationRefreshTrigger): Promise<void>;
  /** Stops all scheduling. Terminal authorization state ends renewal for good. */
  suspend(): void;
  destroy(): void;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function defaultResumeSource(): CollaborationResumeSource | null {
  return typeof globalThis.document === "undefined"
    ? null
    : (globalThis.document as unknown as CollaborationResumeSource);
}

/**
 * Owns the credential of one parent document.
 *
 * The token is issued per parent document, so a `sectioned-v1` document with a
 * root manifest and N sections needs exactly one HTTP refresh, fanned out to
 * N+1 logical rooms over the one shared socket (R5). Renewal is in band: the
 * broker never disconnects, never recreates a provider and never touches a
 * Y.Doc, which is what keeps editing continuous across a lease boundary (R4).
 */
export function createCollaborationCredentialBroker({
  documentId,
  tokenProvider,
  schedule = true,
  now = () => Date.now(),
  random = Math.random,
  resumeSource = defaultResumeSource(),
  onRefreshFailed,
}: CollaborationCredentialBrokerOptions): CollaborationCredentialBroker {
  const targets = new Set<CollaborationCredentialTarget>();

  let credential: CollaborationCredential | null = null;
  /** Local timestamp after which the cached credential must be refreshed. */
  let refreshDueAt = 0;
  let inFlight: Promise<CollaborationCredential> | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let suspended = false;
  let destroyed = false;

  const clearTimer = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };

  const nextDelayMs = (): number => {
    const { clockSkewMs, jitterMs, minIntervalMs, maxIntervalMs, anonymousIntervalMs } =
      CREDENTIAL_REFRESH_BOUNDS;
    // The anonymous credential has no expiry, so it is renewed on a bounded
    // cadence that stays below the server's 60초 lease instead.
    if (typeof credential === "string") return anonymousIntervalMs;
    if (!credential) return minIntervalMs;
    const jitter = Math.floor(random() * jitterMs);
    // `expiresAt` is server time. Clamping the *derived* delay - rather than
    // trusting the difference - is what absorbs clock skew: a client running
    // behind falls back to the ceiling instead of never renewing, and one
    // running ahead falls back to the floor instead of renewing in a loop.
    return clamp(credential.expiresAt - now() - clockSkewMs - jitter, minIntervalMs, maxIntervalMs);
  };

  const armTimer = (delayMs: number) => {
    clearTimer();
    if (!schedule || suspended || destroyed || targets.size === 0) return;
    timer = setTimeout(() => {
      timer = null;
      void refreshAndFanOut("schedule");
    }, delayMs);
  };

  const refresh = (): Promise<CollaborationCredential> => {
    // Suspension is a refusal, not just a stopped clock. Without this guard
    // `suspend()` only cancels the timer, and any path that still reaches a room
    // - the shared socket reopening after a blip and reauthorizing every
    // attached provider - would fetch a fresh token per room, indefinitely.
    if (suspended) return Promise.reject(new CollaborationCredentialSuspendedError(documentId));
    // Single flight: every room that needs a token while one request is open
    // joins that request, so a fan-out costs one HTTP call, not one per room.
    if (inFlight) return inFlight;

    const run = (async () => {
      try {
        const next = await tokenProvider();
        if (destroyed) return next;
        credential = next;
        refreshDueAt = now() + nextDelayMs();
        // The clock starts from the credential, not from registration: before
        // the first token exists there is nothing to renew.
        armTimer(refreshDueAt - now());
        return next;
      } finally {
        inFlight = null;
      }
    })();

    inFlight = run;
    return run;
  };

  const tokenOf = (value: CollaborationCredential): string =>
    typeof value === "string" ? value : value.token;

  const getToken = async (): Promise<string> => {
    // The cached credential is refused as well: handing back a still-unexpired
    // token after revocation would let a room reauthorize on the next socket
    // open, which is the storm this guard exists to stop.
    if (suspended) throw new CollaborationCredentialSuspendedError(documentId);
    if (credential && now() < refreshDueAt) return tokenOf(credential);
    return tokenOf(await refresh());
  };

  async function refreshAndFanOut(trigger: CollaborationRefreshTrigger = "manual"): Promise<void> {
    if (destroyed || suspended) return;
    clearTimer();

    try {
      await refresh();
    } catch (error) {
      if (destroyed) return;
      // Fail closed: the previous lease is never extended by a failed refresh.
      // Retrying on a bounded interval keeps a broken IAM from turning into a
      // request storm (AE5).
      onRefreshFailed?.(error, trigger);
      armTimer(CREDENTIAL_REFRESH_BOUNDS.retryIntervalMs);
      return;
    }

    if (destroyed || suspended) return;

    // The clock is re-armed before the fan-out, so a room that never settles its
    // send cannot stall the next renewal for every other room.
    armTimer(Math.max(0, refreshDueAt - now()));

    // One credential, one send per room, in registration order. There is no
    // success ACK to await; the server's answer to a bad renewal is a logical
    // close, so nothing here waits for a confirmation event.
    await Promise.all([...targets].map((target) => target.sendToken().catch(() => undefined)));
  }

  /**
   * A tab that was suspended may have missed one or more lease boundaries, and
   * anything it sends first would be admitted under a lease the server has
   * already expired. So outbound updates are held, every room reauthorizes, and
   * only then does the *same* Y.Doc resync and the held updates replay.
   */
  const handleResume = async (): Promise<void> => {
    if (destroyed || suspended || targets.size === 0) return;
    if (resumeSource?.visibilityState === "hidden") return;

    const resumed = [...targets];
    for (const target of resumed) target.pauseOutbound?.();
    try {
      await refreshAndFanOut("resume");
    } finally {
      for (const target of resumed) {
        // A room can go terminal while the refresh is in flight: the server
        // refuses it, the provider blocks outbound and unregisters here. It is
        // no longer in `targets`, and resuming it from this captured list would
        // resync a room the server has ended and lift an admission barrier that
        // must stay closed.
        if (!targets.has(target)) continue;
        target.resync?.();
        target.resumeOutbound?.();
      }
    }
  };

  const boundResume = () => void handleResume();
  resumeSource?.addEventListener("visibilitychange", boundResume);
  resumeSource?.addEventListener("pageshow", boundResume);

  return {
    documentId,
    get targetCount() {
      return targets.size;
    },
    get suspended() {
      return suspended;
    },
    getToken,
    register(target) {
      targets.add(target);
      if (!timer && credential) armTimer(Math.max(0, refreshDueAt - now()));
      return () => {
        targets.delete(target);
        if (targets.size === 0) clearTimer();
      };
    },
    refreshAndFanOut,
    /**
     * Ends renewal for this document, permanently. There is deliberately no
     * re-arm path, including for `lease-expired`.
     *
     * `lease-expired` is the one case that can be reached by a transient
     * failure rather than a real revocation: a token endpoint down for longer
     * than the 60초 lease outlives the ~5 bounded retries inside it. A hard stop
     * is still the right answer, because by then the server has already closed
     * the room - recovering would mean re-attaching and re-syncing a new
     * provider, which is a reconnect this layer does not own and cannot express
     * in band (R4 keeps provider, socket and Y.Doc identity stable across a
     * lease boundary precisely by never rebuilding them). Re-arming here would
     * instead poll an endpoint already known to be failing, for a room the
     * server will keep refusing - the storm AE5 forbids.
     *
     * The user is not silently stuck: the terminal state surfaces "인증이
     * 만료되어 편집을 중지했습니다" and U7 offers the refused content for export,
     * so a reload is a visible, lossless recovery. Anything shorter than a lease
     * is already absorbed by the bounded retry, which is R9's continuity claim.
     */
    suspend() {
      suspended = true;
      clearTimer();
    },
    destroy() {
      destroyed = true;
      clearTimer();
      targets.clear();
      resumeSource?.removeEventListener("visibilitychange", boundResume);
      resumeSource?.removeEventListener("pageshow", boundResume);
    },
  };
}
